import {
	Prebid_Vendors,
	Required_Purposes,
} from "@/config/reach/common/constants/consent";
import { headerBiddingStore } from "@/store/headerBiddingStore";
import { prebidStore } from "@/store/prebidStore";
import {
	areDisplaySlotConfigs,
	isAdServerTargeting,
	isPbjs,
	isVideoSlotConfig,
} from "@/ts/guards";
import type { AuctionModule, PrebidModuleConfig } from "@/ts/module";
import type {
	AdUnit,
	AdUnits,
	PageMiscData,
	Pbjs,
	PrebidConfig,
} from "@/ts/prebid/types";
import type {
	BidderSettings,
	BreakpointsSmallAndAbove,
	DisplaySlotConfig,
	Ortb2,
	VideoSlotConfig,
} from "@/ts/types";
import { createDependencyLoader } from "@/utils/dependencyLoader";
import { getWidth } from "@/utils/dom";
import { getModules } from "@/utils/modules";
import {
	addDisplayBidderDataToBids,
	addOrtb2Imp,
	addVideoBidderDataToBids,
	addVideoMediaTypes,
	filterGumgumBidder,
	updatePrebidTargetingToStore,
} from "@/utils/prebidDataHelper";
import { buildTTRTargeting } from "@/utils/prebidTTR";
import {
	findSlotPattern,
	resolveRepeatSlots,
	withAdUnitCode,
} from "@/utils/slots";

const analyticsEnabledPbjsInstances = new WeakSet<object>();

export class Prebid implements AuctionModule {
	private config: PrebidModuleConfig;
	name = "Prebid" as const;

	constructor(config: PrebidModuleConfig) {
		this.config = config;
	}

	async init(): Promise<void> {
		const { actions } = headerBiddingStore.getState();
		actions.addVendor(this.name);
		const [consentModule] = await getModules(["Consent"]);
		const consented = await consentModule.run({
			vendorId: Prebid_Vendors,
			purposes: Required_Purposes,
		});
		if (!consented) {
			return;
		}
		await prebidLoader.ensure(this.config.exposeGlobal ?? false);
	}

	async run(
		auctionId: number,
		commandSlots: (DisplaySlotConfig | VideoSlotConfig)[],
		timeout: number,
	) {
		const { selectors: hbSelectors, actions: hbActions } =
			headerBiddingStore.getState();

		const [consentModule] = await getModules(["Consent"]);
		const consented = consentModule.run({
			vendorId: Prebid_Vendors,
			purposes: Required_Purposes,
			sync: true,
		});
		if (!consented) {
			hbActions.updateVendorAuctionStatus(auctionId, this.name, "failed");
			return;
		}

		const pbjs = await prebidLoader.ensure(this.config.exposeGlobal ?? false);
		if (!isPbjs(pbjs)) {
			hbActions.updateVendorAuctionStatus(auctionId, this.name, "failed");
			return;
		}

		const {
			displayAdUnits,
			videoAdUnits,
			outstreamAdUnits,
			prebidConfig,
			carouselAdUnits,
			bidderSettings,
			enableBidderTTRTracking,
		} = this.config;

		const breakpoint = getWidth();

		// For display ad units, filter out gumgum when on xsmall
		const currBreakpointDisplayAdUnits =
			breakpoint === "xsmall"
				? filterGumgumBidder(displayAdUnits.small)
				: displayAdUnits[breakpoint as BreakpointsSmallAndAbove];
		// For video and outstream, fallback to small config when on xsmall
		const currBreakpointVideoAdUnits =
			breakpoint === "xsmall"
				? videoAdUnits.small
				: videoAdUnits[breakpoint as BreakpointsSmallAndAbove];
		const currBreakpointOutstreamAdUnits =
			breakpoint === "xsmall"
				? outstreamAdUnits.small
				: outstreamAdUnits[breakpoint as BreakpointsSmallAndAbove];

		const joinedDisplayAdUnits = {
			...currBreakpointDisplayAdUnits,
			...currBreakpointOutstreamAdUnits,
			...carouselAdUnits,
		};

		const { ortb2, pageMisc } = hbSelectors.getAuctionData();

		if (commandSlots.length > 0) {
			hbActions.updateVendorAuctionStatus(auctionId, this.name, "running");
			const configAdUnits = areDisplaySlotConfigs(commandSlots)
				? joinedDisplayAdUnits
				: currBreakpointVideoAdUnits;

			const baseAdUnits = getBaseAdUnits(commandSlots, configAdUnits);

			// Extract unique bidder names from the resolved ad units and write to store
			const bidderNames = [
				...new Set(
					baseAdUnits.flatMap((adUnit) => adUnit.bids.map((bid) => bid.bidder)),
				),
			];
			prebidStore.getState().actions.setBidders(auctionId, bidderNames);

			// Read bidder data written to prebidStore by bidder modules during their
			// own init() — no direct calls into other modules needed here.
			const bidderModuleData = prebidStore.getState().selectors.getBidderData();
			const enrichedPageMisc = { ...pageMisc, ...bidderModuleData };

			const adUnits = enrichAdUnits(
				baseAdUnits,
				commandSlots,
				enrichedPageMisc,
			);

			const slotIds = adUnits.map((adUnit) => adUnit.code);

			// Merge our ortb2 with any existing ortb2 (e.g. set by 3rd parties via
			// pbjs_reach.setConfig) so we don't clobber their site.ext.data.
			const existingOrtb2 = pbjs.getConfig("ortb2");
			const mergedOrtb2: Ortb2 = {
				...existingOrtb2,
				...ortb2,
				site: {
					...existingOrtb2?.site,
					...ortb2?.site,
					ext: {
						...existingOrtb2?.site?.ext,
						...ortb2?.site?.ext,
						data: {
							...existingOrtb2?.site?.ext?.data,
							...ortb2?.site?.ext?.data,
						},
					},
				},
				...(ortb2?.user && {
					user: {
						...existingOrtb2?.user,
						...ortb2.user,
					},
				}),
			};

			const config: PrebidConfig = { ...prebidConfig, ortb2: mergedOrtb2 };
			pbjs.que.push(() => {
				enablePianoAnalyticsOnce(pbjs);
				configurePbjs(pbjs, config, bidderSettings);
				pbjs.requestBids({
					adUnits: adUnits,
					bidsBackHandler: auctionCompletionCallback(
						auctionId,
						this.name,
						slotIds,
						pbjs,
						enableBidderTTRTracking,
						timeout,
					),
					timeout,
				});
			});
			pbjs.processQueue();
		}
	}
}

const auctionCompletionCallback =
	(
		auctionId: number,
		vendorName: "Prebid",
		slotIds: string[],
		pbjs: Pbjs,
		enableBidderTTRTracking?: boolean,
		timeout?: number,
	) =>
	(_bids?: unknown, _timedOut?: boolean, _auctionId?: string) => {
		const { actions } = headerBiddingStore.getState();
		const ast = pbjs.getAdserverTargeting();

		if (enableBidderTTRTracking && timeout && _auctionId && pbjs.getEvents) {
			const events = pbjs.getEvents();
			const ttrTargeting = buildTTRTargeting(events, _auctionId, timeout);
			for (const slotId of slotIds) {
				if (ttrTargeting[slotId]) {
					ast[slotId] = { ...ast[slotId], ...ttrTargeting[slotId] };
				}
			}
		}

		pbjs.setPAAPIConfigForGPT();
		if (isAdServerTargeting(ast)) {
			updatePrebidTargetingToStore(auctionId, slotIds, ast);
		}
		actions.updateVendorAuctionStatus(auctionId, vendorName, "done");
	};

const addBidderData = (
	slotConfig: DisplaySlotConfig | VideoSlotConfig,
	adUnit: AdUnit,
	pageMisc: PageMiscData,
) => {
	const isVideoSlot = isVideoSlotConfig(slotConfig);
	const bids = isVideoSlot
		? addVideoBidderDataToBids(adUnit.bids, slotConfig)
		: addDisplayBidderDataToBids(adUnit.bids, slotConfig, pageMisc);
	return {
		...adUnit,
		bids,
		...(isVideoSlot && { mediaTypes: addVideoMediaTypes(adUnit, slotConfig) }),
		...{ ortb2Imp: addOrtb2Imp(slotConfig, adUnit) },
	};
};

const hasMatchingConfig = (
	slotConfig: DisplaySlotConfig | VideoSlotConfig,
	configAdUnits: AdUnits,
) => {
	const slotId = slotConfig.slotId;
	if (configAdUnits[slotId]) {
		return true;
	}
	// Repeat / patterned match
	const pattern = findSlotPattern(slotId);
	if (!pattern) {
		return false;
	}

	const matched = Boolean(configAdUnits[pattern.baseKey]);
	return matched;
};

/** Merge our ortb2 with any existing ortb2 already on pbjs (e.g. set by
 *  3rd parties via pbjs_reach.setConfig) and apply config + bidderSettings.
 *  Must be called inside a pbjs.que.push callback. */
const configurePbjs = (
	pbjs: Pbjs,
	config: PrebidConfig,
	bidderSettings: BidderSettings,
) => {
	const existingOrtb2 = pbjs.getConfig("ortb2");
	const mergedOrtb2: Ortb2 = {
		...existingOrtb2,
		...config.ortb2,
		site: {
			...existingOrtb2?.site,
			...config.ortb2?.site,
			ext: {
				...existingOrtb2?.site?.ext,
				...config.ortb2?.site?.ext,
				data: {
					...existingOrtb2?.site?.ext?.data,
					...config.ortb2?.site?.ext?.data,
				},
			},
		},
		...(config.ortb2?.user && {
			user: {
				...existingOrtb2?.user,
				...config.ortb2.user,
			},
		}),
	};

	pbjs.setConfig(structuredClone({ ...config, ortb2: mergedOrtb2 }));
	pbjs.bidderSettings = {
		...(pbjs.bidderSettings || {}),
		...bidderSettings,
	};
};

const enablePianoAnalyticsOnce = (pbjs: Pbjs) => {
	if (analyticsEnabledPbjsInstances.has(pbjs)) {
		return;
	}

	pbjs.enableAnalytics?.({
		provider: "pianoDmp",
	});
	analyticsEnabledPbjsInstances.add(pbjs);
};

// Expose prebid global variable when configured or in debug mode
const exposePrebidGlobal = (pbjs: Pbjs, configOverride: boolean) => {
	if (typeof window !== "undefined") {
		const queryParams = new URLSearchParams(window.location.search);
		if (configOverride || queryParams.get("pbjs_debug") === "true") {
			window.pbjs_reach = pbjs;

			window._pbjsGlobals = window._pbjsGlobals || [];
			window._pbjsGlobals.push("pbjs_reach");
		}
	}
};

const getBaseAdUnits = (
	commandSlots: (DisplaySlotConfig | VideoSlotConfig)[],
	configAdUnits: AdUnits,
): AdUnit[] => {
	const isDisplayWithContainer =
		areDisplaySlotConfigs(commandSlots) && commandSlots[0].container;
	return isDisplayWithContainer
		? commandSlots.map((slot) => withAdUnitCode(slot.slotId, configAdUnits))
		: commandSlots
				.filter((slot) => hasMatchingConfig(slot, configAdUnits))
				.map((slot) => resolveRepeatSlots(slot.slotId, configAdUnits));
};

const enrichAdUnits = (
	baseAdUnits: AdUnit[],
	commandSlots: (DisplaySlotConfig | VideoSlotConfig)[],
	pageMisc: PageMiscData,
) => {
	return baseAdUnits
		.map((adUnit, key) => addBidderData(commandSlots[key], adUnit, pageMisc))
		.map((adUnit) => {
			if (!adUnit?.mediaTypes?.video) return adUnit;
			return {
				...adUnit,
				mediaTypes: {
					video: {
						...adUnit.mediaTypes.video,
						playbackmethod: [2],
					},
				},
			};
		});
};

// export the dependency-loader so we can reset it for unit testing
export const prebidLoader = createDependencyLoader<Pbjs, [boolean]>(
	async (configOverride) => {
		const [{ default: pbjs }] = await Promise.all([
			import("prebid.js"),
			import("@/utils/prebidBidders"),
		]);
		exposePrebidGlobal(pbjs, configOverride);
		return pbjs;
	},
);
